import {
    ICosmicDailyMetrics,
    ICosmicDailyMetricsByWorkload,
    ICosmicServiceDetailReport,
    ICosmicServiceDetails,
    CosmicCostHierarchyResponse,
    CosmicCostNode,
    CosmicCostAndCoreHierarchyResponse, CosmicCostAndCoreNode,
} from "../models/CosmicModels";
import { getJson, post, postJson } from "../utils/apiServiceBase";
import { parseCosmicSearchFiltersJson, parseFiltersToJson } from "../models/FilterView";

import { CategoryDivision } from "../models/CategoryDivision";
import { Moment } from "moment";
import { QueryableClass } from "../hooks/useCustomQuery";
import { ServiceTreeItem } from "../models/serviceTree";
import moment from "moment";

export interface ICosmicQueryParams {
    pageSize?: number;
    pageToken?: string;
    level?: number;
    parentPath?: string[];
    filters?: {
        workloadIds?: string[];
        categories?: string[];
        minCost?: number;
        maxCost?: number;
    };
}
// cache time: 5 mins
@QueryableClass({ cacheTime: 1800000, retry: 3 })
export class CosmicService {
    static async getTopNCosmicDailyMetricsByWorkload(start: Moment, end: Moment, cosmicSearchFilters: string, filterData: Partial<Record<CategoryDivision, string[]>>, serviceIdMap: Map<string, ServiceTreeItem>, n: number, metricType: string) {
        const url = `api/v1.0/cosmic/topNCosmicWorkloadDailyMetric?startDate=${start.format('YYYY-MM-DD')}&endDate=${end.format('YYYY-MM-DD')}&metricType=${metricType}&n=${n}`;
        const data: ICosmicDailyMetricsByWorkload[] = await postJson(url, {
            cosmicSearchFilters: parseCosmicSearchFiltersJson(cosmicSearchFilters),
            searchFilters: parseFiltersToJson(filterData, serviceIdMap)
        });
        return data;
    }

    // TODO: remove this method after integrating cogsAttribution.
    static async getCosmicDailyCost(start: Moment, end: Moment, cosmicSearchFilters: string, filterData: Partial<Record<CategoryDivision, string[]>>, serviceIdMap: Map<string, ServiceTreeItem>) {
        const url = `api/v1.0/cosmic/totalCogs?startDate=${start.format('YYYY-MM-DD')}&endDate=${end.format('YYYY-MM-DD')}`;
        const data: ICosmicDailyMetrics[] = await postJson(url, {
            cosmicSearchFilters: parseCosmicSearchFiltersJson(cosmicSearchFilters),
            searchFilters: parseFiltersToJson(filterData, serviceIdMap)
        });
        return data;
    }

    static async getCosmicServiceDimensionMetric(start: Moment, end: Moment, cosmicSearchFilters: string, globalFilters: Partial<Record<CategoryDivision, string[]>>, serviceIdMap: Map<string, ServiceTreeItem>, metricType: string) {
        const url = `api/v1.0/cosmic/cosmicServiceDimensionMetric?startDate=${start.format('YYYY-MM-DD')}&endDate=${end.format('YYYY-MM-DD')}&metricType=${metricType}`;
        return await postJson(url, {
            cosmicSearchFilters: parseCosmicSearchFiltersJson(cosmicSearchFilters),
            searchFilters: parseFiltersToJson(globalFilters, serviceIdMap)
        });
    }

    static async getCosmicWorkloadDailyAppMetric(start: Moment, end: Moment, cosmicSearchFilters: string, globalFilters: Partial<Record<CategoryDivision, string[]>>, serviceIdMap: Map<string, ServiceTreeItem>, metricType: string) {
        const url = `api/v1.0/cosmic/cosmicWorkloadDailyAppMetric?startDate=${start.format('YYYY-MM-DD')}&endDate=${end.format('YYYY-MM-DD')}&metricType=${metricType}`;
        return await postJson(url, {
            cosmicSearchFilters: parseCosmicSearchFiltersJson(cosmicSearchFilters),
            searchFilters: parseFiltersToJson(globalFilters, serviceIdMap)
        });
    }

    static async getCosmicServiceDetails(start: Moment, end: Moment, cosmicSearchFilters: string, globalFilters: Partial<Record<CategoryDivision, string[]>>, serviceIdMap: Map<string, ServiceTreeItem>, limit?: number, orderBy?: string, desc?: boolean) : Promise<ICosmicServiceDetailReport<ICosmicServiceDetails>>{
        let url = `api/v1.0/cosmic/cosmicServiceDetails?startDate=${start.format('YYYY-MM-DD')}&endDate=${end.format('YYYY-MM-DD')}`;

        if (limit) url += `&limit=${limit}`;
        if (orderBy) url += `&orderBy=${orderBy}`;
        if (desc) url += `&desc=${desc}`;

        return await postJson(url, {
            cosmicSearchFilters: parseCosmicSearchFiltersJson(cosmicSearchFilters),
            searchFilters: parseFiltersToJson(globalFilters, serviceIdMap)
        });
    }

    static async downloadCosmicServiceDetails(start: Moment, end: Moment, cosmicSearchFilters: string, selectedFilters: string, globalFilters: Partial<Record<CategoryDivision, string[]>>, serviceIdMap: Map<string, ServiceTreeItem>) : Promise<
        {
            data: Blob,
            type: string,
            size: number,
            fileName: string
        }
    >{
        const url = `api/v1.0/cosmic/downloadCosmicServiceDetails?startDate=${start.format('YYYY-MM-DD')}&endDate=${end.format('YYYY-MM-DD')}&selectedFilters=${selectedFilters}`;
        const response = await post(url, {
            cosmicSearchFilters: parseCosmicSearchFiltersJson(cosmicSearchFilters),
            searchFilters: parseFiltersToJson(globalFilters, serviceIdMap)
        });
        return {
            data: await response.blob(),
            type: response.headers.get('content-type') || 'application/octet-stream',
            size: parseInt(response.headers.get('content-length') || '0'),
            fileName: response.headers.get('content-disposition')?.split('filename=')[1] || `cosmic_service_details_${start.format('YYYY-MM-DD')}_${end.format('YYYY-MM-DD')}.xlsx`
        }
    }

    static async getCosmicPlatformMetric(start: Moment, end: Moment, cosmicSearchFilters: string, globalFilters: Partial<Record<CategoryDivision, string[]>>, serviceIdMap: Map<string, ServiceTreeItem>, Legend: string) {
        const url = `api/v1.0/cosmic/cosmicPlatformMetric?startDate=${start.format('YYYY-MM-DD')}&endDate=${end.format('YYYY-MM-DD')}&legend=${Legend}`;
        return await postJson(url, {
            cosmicSearchFilters: parseCosmicSearchFiltersJson(cosmicSearchFilters),
            searchFilters: parseFiltersToJson(globalFilters, serviceIdMap)
        });
    }

    static async getCosmicCostHierarchy(start: Moment, end: Moment, month:string, cosmicSearchFilters: string, globalFilters: Partial<Record<CategoryDivision, string[]>>, serviceIdMap: Map<string, ServiceTreeItem>, path: string): Promise<CosmicCostHierarchyResponse> {
        if (moment(month, "YYYY-MM").isValid()) {
            const monthMoment = moment(month, "YYYY-MM");
            start = monthMoment;
            end = end < monthMoment.clone().endOf("month") ? end : monthMoment.clone().endOf("month");
        }
        const url = `api/v1.0/cosmic/cosmicCostHierarchy?startDate=${start.format('YYYY-MM-DD')}&endDate=${end.format('YYYY-MM-DD')}&path=${path}`;

        const data = await postJson(url, {
            cosmicSearchFilters: parseCosmicSearchFiltersJson(cosmicSearchFilters),
            searchFilters: parseFiltersToJson(globalFilters, serviceIdMap)
        }) as CosmicCostNode[];
        const totalCost = data.reduce((sum, workload) => sum + (workload.cost || 0), 0);
        data.sort((a, b) => (b.cost || 0) - (a.cost || 0));
        return {
            totalCost: totalCost,
            data: data
        };
    }

    static async downloadCosmicCostHierarchy(start: Moment, end: Moment, month:string, metricType:string, cosmicSearchFilters: string, globalFilters: Partial<Record<CategoryDivision, string[]>>, serviceIdMap: Map<string, ServiceTreeItem>): Promise<
        {
            data: Blob,
            type: string,
            size: number,
            fileName: string
        }
    >{
        if (moment(month, "YYYY-MM").isValid()) {
            const monthMoment = moment(month, "YYYY-MM");
            start = monthMoment;
            end = end < monthMoment.clone().endOf("month") ? end : monthMoment.clone().endOf("month");
        }
        const url = `api/v1.0/cosmic/downloadCosmicCostHierarchy?startDate=${start.format('YYYY-MM-DD')}&endDate=${end.format('YYYY-MM-DD')}&metricType=${metricType}`;

        const response = await post(url, {
            cosmicSearchFilters: parseCosmicSearchFiltersJson(cosmicSearchFilters),
            searchFilters: parseFiltersToJson(globalFilters, serviceIdMap)
        });
        return {
            data: await response.blob(),
            type: response.headers.get('content-type') || 'application/octet-stream',
            size: parseInt(response.headers.get('content-length') || '0'),
            fileName: response.headers.get('content-disposition')?.split('filename=')[1] || `monthly_${metricType}_details_${start.format('YYYY-MM-DD')}_${end.format('YYYY-MM-DD')}.csv`
        }
    }

    static async getCosmicCostAndCoreHierarchy(start: Moment, end: Moment, month:string, cosmicSearchFilters: string, globalFilters: Partial<Record<CategoryDivision, string[]>>, serviceIdMap: Map<string, ServiceTreeItem>, path: string): Promise<CosmicCostAndCoreHierarchyResponse> {
        if (moment(month, "YYYY-MM").isValid()) {
            const monthMoment = moment(month, "YYYY-MM");
            start = monthMoment;
            end = end < monthMoment.clone().endOf("month") ? end : monthMoment.clone().endOf("month");
        }
        const url = `api/v1.0/cosmic/cosmicCostAndCoreHierarchy?startDate=${start.format('YYYY-MM-DD')}&endDate=${end.format('YYYY-MM-DD')}&path=${path}`;

        const data = await postJson(url, {
            cosmicSearchFilters: parseCosmicSearchFiltersJson(cosmicSearchFilters),
            searchFilters: parseFiltersToJson(globalFilters, serviceIdMap)
        }) as CosmicCostAndCoreNode[];
        const totalCost = data.reduce((sum, workload) => sum + (workload.cost || 0), 0);
        const totalAppCores = data.reduce((sum, workload) => sum + (workload.appCores || 0), 0);
        const totalFragmentedCores = data.reduce((sum, workload) => sum + (workload.fragmentationCores || 0), 0);
        const totalEmptyAppCores = data.reduce((sum, workload) => sum + (workload.emptyAppCores || 0), 0);
        const totalCores = data.reduce((sum, workload) => sum + (workload.totalCores || 0), 0);
        const totalPricePerCore = totalCost / (totalCores || 1);
        data.sort((a, b) => (b.cost || 0) - (a.cost || 0));
        return {
            totalCost: totalCost,
            totalAppCores: totalAppCores,
            totalFragmentationCores: totalFragmentedCores,
            totalEmptyAppCores: totalEmptyAppCores,
            totalCores: totalCores,
            totalPricePerCore: totalPricePerCore,
            data: data
        };
    }

    static async getMonthlyCosmicCostByCategory(start: Moment, end: Moment, cosmicSearchFilters: string, globalFilters: Partial<Record<CategoryDivision, string[]>>, serviceIdMap: Map<string, ServiceTreeItem>) {
        const url = `api/v1.0/cosmic/cosmicMonthlyCost?startDate=${start.format('YYYY-MM-DD')}&endDate=${end.format('YYYY-MM-DD')}`;
        return await postJson(url, {
            cosmicSearchFilters: parseCosmicSearchFiltersJson(cosmicSearchFilters),
            searchFilters: parseFiltersToJson(globalFilters, serviceIdMap)
        });
    }

    static async getDailyCosmicTotalCost(start: Moment, end: Moment, cosmicSearchFilters: string, globalFilters: Partial<Record<CategoryDivision, string[]>>, serviceIdMap: Map<string, ServiceTreeItem>) {
        const url = `api/v1.0/agent/cosmic/dailycost?startDate=${start.format('YYYY-MM-DD')}&endDate=${end.format('YYYY-MM-DD')}`;
        return await postJson(url, {
            cosmicSearchFilters: parseCosmicSearchFiltersJson(cosmicSearchFilters),
            searchFilters: parseFiltersToJson(globalFilters, serviceIdMap)
        });
    }
}
